import type { QueryFunctionContext, QueryKey } from '@tanstack/react-query';

import { useInfiniteQueryWithCursor } from '../../../ReactQuery/index';
import { get } from '../../../redux/api';
import type { User } from '../../../../shared/model-typings';
import type { CursorPaginatedResponse } from '../../../../shared/types/routes/pagination';

type AvailableServiceAccountsFilter = {
  providerId: number;
  teamId?: number;
};

/**
 * Fetch available service accounts for OIDC trust relationships with cursor pagination.
 */
function fetchAvailableServiceAccounts(
  { pageParam }: QueryFunctionContext<QueryKey, number | undefined>,
  filter: AvailableServiceAccountsFilter,
  pageSize: number,
) {
  const cursor = pageParam ?? 0;
  const { providerId, teamId } = filter;
  return get<CursorPaginatedResponse<Pick<User, 'id' | 'username' | 'email'>, number>>(
    `/api/oidc/providers/${providerId}/available-service-accounts`,
    false,
    {
      pageSize,
      prev: cursor,
      ...(teamId !== undefined && { teamId }),
    },
  );
}

export const AVAILABLE_SERVICE_ACCOUNTS_QUERY_KEY = 'oidc-available-service-accounts';

const getQueryKey = (filter: AvailableServiceAccountsFilter, pageSize: number): QueryKey =>
  [AVAILABLE_SERVICE_ACCOUNTS_QUERY_KEY, filter.providerId, filter.teamId, pageSize];

/**
 * Hook to fetch available service accounts for OIDC trust relationships with cursor-based infinite pagination.
 * Pass providerId (required) and teamId (optional) to filter results.
 */
export default function useAvailableServiceAccounts(
  filter: AvailableServiceAccountsFilter,
  pageSize: number = 50,
  shouldFetch: boolean = true,
) {
  return useInfiniteQueryWithCursor<CursorPaginatedResponse<Pick<User, 'id' | 'username' | 'email'>, number>>({
    queryKey: getQueryKey(filter, pageSize),
    queryFn: (context) => fetchAvailableServiceAccounts(context, filter, pageSize),
    staleTime: 5 * 60 * 1000,
    enabled: shouldFetch,
  });
}
